import { 
  Trend, 
  CustomAlert, 
  AlertHistory, 
  Report, 
  ApiUsage, 
  CustomDashboard, 
  RealtimeData,
  PaginatedResponse,
  TrendFilters,
  TrendAnalytics,
  ViralPrediction,
  MomentumAnalysis,
  BusinessInsights,
  AudienceAnalysis,
  ForecastData,
  CustomDataSource,
  CustomModel,
  TrainingJob,
  ModelPerformance,
  ReportTemplate,
  UsageAnalytics,
  RateLimits,
  User
} from '../types';

export class ApiService {
  private baseUrl: string;
  private authToken: string;

  constructor() {
    // Environment-aware API URL configuration
    const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
    
    if (environment === 'production') {
      // Production: Use the production API URL
      this.baseUrl = process.env.REACT_APP_API_URL || 'https://api.trendbeat.twokai.com';
    } else {
      // Development: Use localhost or fallback
      this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    }
    
    this.authToken = localStorage.getItem('authToken') || '';
  }

  // HTTP Methods
  async get<T>(endpoint: string, params?: any): Promise<T> {
    const queryParams = params ? new URLSearchParams(params).toString() : '';
    const url = queryParams ? `${endpoint}?${queryParams}` : endpoint;
    return this.request<T>(url);
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Refresh auth token from localStorage in case it was updated
    this.authToken = localStorage.getItem('authToken') || '';
    
    // Handle headers properly
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Convert options.headers to Record<string, string>
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          baseHeaders[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          baseHeaders[key] = value;
        });
      } else if (typeof options.headers === 'object') {
        Object.assign(baseHeaders, options.headers);
      }
    }
    
    const headers = baseHeaders;

    // Add auth token if available
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        // Handle 401 Unauthorized - try to refresh token
        if (response.status === 401) {
          const refreshToken = localStorage.getItem('supabase_refresh_token');
          if (refreshToken) {
            try {
              const refreshResponse = await this.post<{access_token: string, refresh_token: string, user: any}>('/api/v1/auth/refresh', {
                refresh_token: refreshToken
              });
              
              // Update tokens in localStorage
              localStorage.setItem('authToken', refreshResponse.access_token);
              localStorage.setItem('supabase_refresh_token', refreshResponse.refresh_token);
              
              // Update the auth token for this request
              this.authToken = refreshResponse.access_token;
              
              // Retry the original request with the new token
              const retryResponse = await fetch(url, {
                ...options,
                headers: {
                  ...headers,
                  'Authorization': `Bearer ${refreshResponse.access_token}`
                }
              });
              
              if (retryResponse.ok) {
                return await retryResponse.json();
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              // If refresh fails, clear tokens and redirect to login
              localStorage.removeItem('authToken');
              localStorage.removeItem('supabase_refresh_token');
              window.location.href = '/login';
              throw new Error('Session expired. Please log in again.');
            }
          } else {
            // No refresh token available, redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('supabase_refresh_token');
            window.location.href = '/login';
            throw new Error('Session expired. Please log in again.');
          }
        }
        
        // Try to get the actual error message from the response
        let errorMessage = `API request failed: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // If we can't parse the error response, use the status text
          errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Trend Services
  async getTrends(filters?: TrendFilters): Promise<PaginatedResponse<Trend>> {
    const queryParams = filters ? new URLSearchParams(filters as any).toString() : '';
    return this.request<PaginatedResponse<Trend>>(`/api/v1/trends?${queryParams}`);
  }

  async getTrendById(id: string): Promise<Trend> {
    return this.request<Trend>(`/api/v1/trends/${id}`);
  }

  async getTrendAnalytics(id: string): Promise<TrendAnalytics> {
    return this.request<TrendAnalytics>(`/api/v1/trends/${id}/analytics`);
  }

  async compareTrends(trendIds: string[]): Promise<any[]> {
    return this.request<any[]>(`/api/v1/trends/compare`, {
      method: 'POST',
      body: JSON.stringify({ trend_ids: trendIds }),
    });
  }

  async getViralPredictions(): Promise<{ viral_predictions: ViralPrediction[]; total_predictions: number; min_viral_score: number; summary: any }> {
    return this.request<{ viral_predictions: ViralPrediction[]; total_predictions: number; min_viral_score: number; summary: any }>('/analytics/viral-predictions');
  }

  async getMomentumAnalysis(): Promise<{ momentum_analysis: MomentumAnalysis[]; total_trends: number; summary: any }> {
    return this.request<{ momentum_analysis: MomentumAnalysis[]; total_trends: number; summary: any }>('/analytics/momentum');
  }

  async getForecastData(trendId: string): Promise<ForecastData> {
    return this.request<ForecastData>(`/trends/${trendId}/forecast`);
  }

  async getBusinessInsights(trendId: string): Promise<BusinessInsights> {
    return this.request<BusinessInsights>(`/trends/${trendId}/business-insights`);
  }

  async getAudienceAnalysis(trendId: string): Promise<AudienceAnalysis> {
    return this.request<AudienceAnalysis>(`/trends/${trendId}/audience`);
  }

  // Alert Services
  async getAlerts(): Promise<CustomAlert[]> {
    return this.request<CustomAlert[]>('/alerts');
  }

  async createAlert(alert: Partial<CustomAlert>): Promise<CustomAlert> {
    return this.request<CustomAlert>('/alerts', {
      method: 'POST',
      body: JSON.stringify(alert),
    });
  }

  async updateAlert(id: string, alert: Partial<CustomAlert>): Promise<CustomAlert> {
    return this.request<CustomAlert>(`/alerts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(alert),
    });
  }

  async deleteAlert(id: string): Promise<void> {
    return this.request<void>(`/alerts/${id}`, {
      method: 'DELETE',
    });
  }

  async getAlertHistory(): Promise<AlertHistory[]> {
    return this.request<AlertHistory[]>('/alerts/history');
  }

  async testAlert(id: string): Promise<boolean> {
    return this.request<boolean>(`/alerts/${id}/test`, {
      method: 'POST',
    });
  }

  // Report Services
  async getReports(): Promise<Report[]> {
    return this.request<Report[]>('/reports');
  }

  async generateReport(type: string, config: any): Promise<Report> {
    return this.request<Report>('/reports/generate', {
      method: 'POST',
      body: JSON.stringify({ type, config }),
    });
  }

  async downloadReport(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/reports/${id}/download`, {
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
      },
    });
    return response.blob();
  }

  async scheduleReport(config: any): Promise<Report> {
    return this.request<Report>('/reports/schedule', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async getReportTemplates(): Promise<ReportTemplate[]> {
    return this.request<ReportTemplate[]>('/reports/templates');
  }

  // Dashboard Services
  async getDashboards(): Promise<CustomDashboard[]> {
    return this.request<CustomDashboard[]>('/dashboards');
  }

  async createDashboard(dashboard: Partial<CustomDashboard>): Promise<CustomDashboard> {
    return this.request<CustomDashboard>('/dashboards', {
      method: 'POST',
      body: JSON.stringify(dashboard),
    });
  }

  async updateDashboard(id: string, dashboard: Partial<CustomDashboard>): Promise<CustomDashboard> {
    return this.request<CustomDashboard>(`/dashboards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dashboard),
    });
  }

  async deleteDashboard(id: string): Promise<void> {
    return this.request<void>(`/dashboards/${id}`, {
      method: 'DELETE',
    });
  }

  async getDashboardData(dashboardId: string): Promise<any> {
    return this.request<any>(`/dashboards/${dashboardId}/data`);
  }

  // API Usage Services
  async getApiUsage(): Promise<ApiUsage> {
    return this.request<ApiUsage>('/api-usage');
  }

  async getUsageAnalytics(): Promise<UsageAnalytics> {
    return this.request<UsageAnalytics>('/usage-analytics');
  }

  async getRateLimits(): Promise<RateLimits> {
    return this.request<RateLimits>('/rate-limits');
  }

  // Realtime Services
  async connectRealtime(): Promise<any> {
    return this.request<any>('/realtime/connect');
  }

  async getRealtimeData(): Promise<RealtimeData[]> {
    return this.request<RealtimeData[]>('/realtime/data');
  }

  async subscribeToAlerts(callback: (alert: AlertHistory) => void): Promise<void> {
    return this.request<void>('/realtime/subscribe', {
      method: 'POST',
      body: JSON.stringify({ callback }),
    });
  }

  // Custom Data Source Services
  async getCustomDataSources(): Promise<CustomDataSource[]> {
    return this.request<CustomDataSource[]>('/custom-data-sources');
  }

  async createCustomDataSource(source: Partial<CustomDataSource>): Promise<CustomDataSource> {
    return this.request<CustomDataSource>('/custom-data-sources', {
      method: 'POST',
      body: JSON.stringify(source),
    });
  }

  async testCustomDataSource(id: string): Promise<boolean> {
    return this.request<boolean>(`/custom-data-sources/${id}/test`, {
      method: 'POST',
    });
  }

  // Custom Model Services
  async getCustomModels(): Promise<CustomModel[]> {
    return this.request<CustomModel[]>('/custom-models');
  }

  async createCustomModel(model: Partial<CustomModel>): Promise<CustomModel> {
    return this.request<CustomModel>('/custom-models', {
      method: 'POST',
      body: JSON.stringify(model),
    });
  }

  async trainCustomModel(id: string): Promise<TrainingJob> {
    return this.request<TrainingJob>(`/custom-models/${id}/train`, {
      method: 'POST',
    });
  }

  async getModelPerformance(id: string): Promise<ModelPerformance> {
    return this.request<ModelPerformance>(`/custom-models/${id}/performance`);
  }

  // User Services
  async getCurrentUser(): Promise<any> {
    return this.request<any>('/api/v1/users/profile');
  }

  async updateUserProfile(profileData: Partial<User>): Promise<any> {
    return this.request('/api/v1/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async getUserPreferences(): Promise<any> {
    return this.request<any>('/users/preferences');
  }

  async updateUserPreferences(preferences: any): Promise<any> {
    return this.request<any>('/users/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Company Services (Billing Purposes Only)
  async getUserCompany(): Promise<any | null> {
    return this.request<any>('/api/v1/users/company');
  }

  async createUserCompany(company: any): Promise<any> {
    return this.request<any>('/api/v1/users/company', {
      method: 'POST',
      body: JSON.stringify(company),
    });
  }

  async updateUserCompany(company: any): Promise<any> {
    return this.request<any>('/api/v1/users/company', {
      method: 'PUT',
      body: JSON.stringify(company),
    });
  }

  async deleteUserCompany(): Promise<void> {
    return this.request<void>('/api/v1/users/company', {
      method: 'DELETE',
    });
  }

  // Security Services
  async changePassword(passwordData: any): Promise<any> {
    return this.request<any>('/api/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  async enableTwoFactor(): Promise<any> {
    return this.request<any>('/users/security/2fa/enable', {
      method: 'POST',
    });
  }

  async verifyTwoFactor(totpCode: string): Promise<any> {
    return this.request<any>('/users/security/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ totp_code: totpCode }),
    });
  }

  async disableTwoFactor(): Promise<any> {
    return this.request<any>('/users/security/2fa/disable', {
      method: 'POST',
    });
  }

  // FIDO2 Services
  async getFIDO2Status(): Promise<any> {
    return this.request<any>('/users/security/fido2/status');
  }

  async startFIDO2Registration(options: any): Promise<any> {
    return this.request<any>('/users/security/fido2/register/start', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async completeFIDO2Registration(data: any): Promise<any> {
    return this.request<any>('/users/security/fido2/register/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async startFIDO2Authentication(options: any): Promise<any> {
    return this.request<any>('/users/security/fido2/authenticate/start', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async completeFIDO2Authentication(data: any): Promise<any> {
    return this.request<any>('/users/security/fido2/authenticate/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFIDO2Credentials(): Promise<any[]> {
    return this.request<any[]>('/users/security/fido2/credentials');
  }

  async removeFIDO2Credential(credentialId: string): Promise<any> {
    return this.request<any>(`/users/security/fido2/credentials/${credentialId}`, {
      method: 'DELETE',
    });
  }

  // Audit Services
  async getAuditLogs(filters?: any): Promise<any[]> {
    return this.request<any[]>('/audit/logs', {
      method: 'POST',
      body: JSON.stringify(filters || {}),
    });
  }

  async getUserActivitySummary(days: number = 30): Promise<any> {
    return this.request<any>(`/audit/activity-summary?days=${days}`);
  }

  async getSecuritySummary(days: number = 30): Promise<any> {
    return this.request<any>(`/audit/security-summary?days=${days}`);
  }

  async exportAuditLogs(format: string = 'json', startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams({ format });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request<any>(`/audit/export?${params.toString()}`);
  }

  // Session Services
  async getUserSessions(): Promise<any[]> {
    return this.request<any[]>('/users/sessions');
  }

  async revokeUserSession(sessionId: string): Promise<any> {
    return this.request<any>(`/users/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async revokeAllUserSessions(): Promise<any> {
    return this.request<any>('/users/sessions', {
      method: 'DELETE',
    });
  }

  // API Key Management
  async getAPIKeys(organizationId?: string): Promise<any[]> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    return this.request<any[]>(`/api/v1/api-keys/${params}`);
  }

  async createAPIKey(data: any): Promise<any> {
    return this.request<any>('/api/v1/api-keys/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAPIKey(keyId: string, data: any): Promise<any> {
    return this.request<any>(`/api/v1/api-keys/${keyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async regenerateAPIKey(keyId: string): Promise<any> {
    return this.request<any>(`/api/v1/api-keys/${keyId}/regenerate`, {
      method: 'POST',
    });
  }

  async revokeAPIKey(keyId: string): Promise<any> {
    return this.request<any>(`/api/v1/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  // API Usage Analytics
  async getUsageSummary(organizationId?: string): Promise<any> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    return this.request<any>(`/api-usage/summary${params}`);
  }

  async getUsageTrends(days: number = 30, organizationId?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    if (organizationId) params.append('organization_id', organizationId);
    return this.request<any>(`/api-usage/trends?${params}`);
  }

  async getEndpointBreakdown(days: number = 30, organizationId?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    if (organizationId) params.append('organization_id', organizationId);
    return this.request<any>(`/api-usage/breakdown?${params}`);
  }

  async exportUsageData(days: number = 30, format: string = 'json', organizationId?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    params.append('format', format);
    if (organizationId) params.append('organization_id', organizationId);
    return this.request<any>(`/api-usage/export?${params}`);
  }

  // Data Export Services
  async exportUserData(): Promise<any> {
    return this.request<any>('/users/export-data', {
      method: 'POST',
    });
  }

  async deleteUserAccount(): Promise<any> {
    return this.request<any>('/api/v1/users/delete-account', {
      method: 'DELETE',
    });
  }

  // Workspace Services
  async getWorkspaceSettings(): Promise<any> {
    return this.request<any>('/workspace/settings');
  }

  async updateWorkspaceSettings(settings: any): Promise<any> {
    return this.request<any>('/workspace/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // White Label Services
  async getWhiteLabelConfig(): Promise<any> {
    return this.request<any>('/workspace/white-label');
  }

  async updateWhiteLabelConfig(config: any): Promise<any> {
    return this.request<any>('/workspace/white-label', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Integration Services
  async getIntegrations(): Promise<any[]> {
    return this.request<any[]>('/integrations');
  }

  async createIntegration(integration: any): Promise<any> {
    return this.request<any>('/integrations', {
      method: 'POST',
      body: JSON.stringify(integration),
    });
  }

  async testIntegration(id: string): Promise<boolean> {
    return this.request<boolean>(`/integrations/${id}/test`, {
      method: 'POST',
    });
  }

  // Analytics Services
  async getPlatformAnalytics(): Promise<{ platform_analytics: any; total_platforms: number; summary: any }> {
    return this.request<{ platform_analytics: any; total_platforms: number; summary: any }>('/analytics/platform-performance');
  }

  async getCategoryAnalytics(): Promise<{ category_analytics: any; total_categories: number; summary: any }> {
    return this.request<{ category_analytics: any; total_categories: number; summary: any }>('/analytics/categories');
  }

  async getCompetitiveAnalysis(): Promise<{ competitive_analysis: any; direct_competitors: any[]; market_gaps: string[]; summary: any }> {
    return this.request<{ competitive_analysis: any; direct_competitors: any[]; market_gaps: string[]; summary: any }>('/analytics/competitive');
  }

  async getMarketInsights(): Promise<{ market_insights: any; opportunities: any[]; risks: any[]; summary: any }> {
    return this.request<{ market_insights: any; opportunities: any[]; risks: any[]; summary: any }>('/analytics/market-insights');
  }

  // Data Export Services
  async exportData(type: string, filters: any): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/export/${type}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authToken}`,
      },
      body: JSON.stringify(filters),
    });
    return response.blob();
  }

  // System Services
  async getDataQualityMetrics(): Promise<any> {
    return this.request<any>('/system/data-quality');
  }

  async getDataFreshness(): Promise<any> {
    return this.request<any>('/system/data-freshness');
  }

  async getSystemHealth(): Promise<any> {
    return this.request<any>('/system/health');
  }

  async getPipelineStatus(): Promise<any> {
    return this.request<any>('/system/pipeline-status');
  }

  async getPerformanceMetrics(): Promise<any> {
    return this.request<any>('/system/performance');
  }

  // Notification Services
  async getNotificationSettings(): Promise<any> {
    return this.request<any>('/api/v1/users/notification-preferences');
  }

  async updateNotificationSettings(settings: any): Promise<any> {
    return this.request<any>('/api/v1/users/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async createNotificationSettings(settings: any): Promise<any> {
    // The GET endpoint automatically creates default preferences if none exist
    // So we just call the GET endpoint to trigger creation
    return this.request<any>('/api/v1/users/notification-preferences');
  }

  // Billing Services
  async getBillingHistory(): Promise<any> {
    return this.request<any>('/api/v1/billing/history');
  }

  // Notification Management
  async getNotifications(userId: string, options?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
    category?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.unreadOnly) params.append('unread_only', 'true');
    if (options?.category) params.append('category', options.category);
    
    return this.request<any>(`/api/v1/users/${userId}/notifications?${params}`);
  }
  
  async markNotificationRead(userId: string, notificationId: string): Promise<any> {
    return this.request<any>(`/api/v1/users/${userId}/notifications/${notificationId}/read`, {
      method: 'PATCH'
    });
  }

  async markNotificationViewed(userId: string, notificationId: string): Promise<any> {
    return this.request<any>(`/api/v1/users/${userId}/notifications/${notificationId}/viewed`, {
      method: 'PATCH'
    });
  }
  
  async markAllNotificationsRead(userId: string, category?: string): Promise<any> {
    const body = category ? { category } : {};
    return this.request<any>(`/api/v1/users/${userId}/notifications/read-all`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }
  
  async clearNotifications(userId: string, category?: string): Promise<any> {
    const body = category ? { category } : {};
    return this.request<any>(`/api/v1/users/${userId}/notifications/clear`, {
      method: 'DELETE',
      body: JSON.stringify(body)
    });
  }
  
  async getUnreadCount(userId: string): Promise<any> {
    return this.request<any>(`/api/v1/users/${userId}/notifications/unread-count`);
  }

  async sendTestNotification(type: string): Promise<boolean> {
    return this.request<boolean>('/notifications/test', {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  }

  // Search Services
  async searchTrends(query: string, filters?: any): Promise<PaginatedResponse<Trend>> {
    return this.request<PaginatedResponse<Trend>>('/trends/search', {
      method: 'POST',
      body: JSON.stringify({ query, filters }),
    });
  }

  async getTrendSuggestions(query: string): Promise<string[]> {
    return this.request<string[]>(`/trends/suggestions?query=${encodeURIComponent(query)}`);
  }

  async getRelatedTrends(trendId: string): Promise<Trend[]> {
    return this.request<Trend[]>(`/trends/${trendId}/related`);
  }

  // Batch Operations
  async batchCreateAlerts(alerts: Partial<CustomAlert>[]): Promise<CustomAlert[]> {
    return this.request<CustomAlert[]>('/alerts/batch', {
      method: 'POST',
      body: JSON.stringify({ alerts }),
    });
  }

  async batchUpdateTrends(trends: Partial<Trend>[]): Promise<Trend[]> {
    return this.request<Trend[]>('/trends/batch', {
      method: 'PUT',
      body: JSON.stringify({ trends }),
    });
  }

  // Utility Methods
  setAuthToken(token: string): void {
    this.authToken = token;
    localStorage.setItem('authToken', token);
  }

  clearAuthToken(): void {
    this.authToken = '';
    localStorage.removeItem('authToken');
  }

  isAuthenticated(): boolean {
    return !!this.authToken;
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Export individual methods for convenience
export const {
  getTrends,
  getTrendById,
  getTrendAnalytics,
  compareTrends,
  getViralPredictions,
  getMomentumAnalysis,
  getForecastData,
  getBusinessInsights,
  getAudienceAnalysis,
  getAlerts,
  createAlert,
  updateAlert,
  deleteAlert,
  getAlertHistory,
  testAlert,
  getReports,
  generateReport,
  downloadReport,
  scheduleReport,
  getReportTemplates,
  getDashboards,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  getDashboardData,
  getApiUsage,
  getUsageAnalytics,
  getRateLimits,
  connectRealtime,
  getRealtimeData,
  subscribeToAlerts,
  getCustomDataSources,
  createCustomDataSource,
  testCustomDataSource,
  getCustomModels,
  createCustomModel,
  trainCustomModel,
  getModelPerformance,
  getCurrentUser,
  updateUserProfile,
  getUserPreferences,
  updateUserPreferences,
  // Phase 2: Enhanced User Management
  getUserCompany,
  createUserCompany,
  updateUserCompany,
  deleteUserCompany,
  changePassword,
  enableTwoFactor,
  verifyTwoFactor,
  disableTwoFactor,
  getUserSessions,
  revokeUserSession,
  revokeAllUserSessions,
  exportUserData,
  deleteUserAccount,
  getWorkspaceSettings,
  updateWorkspaceSettings,
  getWhiteLabelConfig,
  updateWhiteLabelConfig,
  getIntegrations,
  createIntegration,
  testIntegration,
  getPlatformAnalytics,
  getCategoryAnalytics,
  getCompetitiveAnalysis,
  getMarketInsights,
  exportData,
  getDataQualityMetrics,
  getDataFreshness,
  getSystemHealth,
  getPipelineStatus,
  getPerformanceMetrics,
  getNotificationSettings,
  updateNotificationSettings,
  createNotificationSettings,
  sendTestNotification,
  searchTrends,
  getTrendSuggestions,
  getRelatedTrends,
  batchCreateAlerts,
  batchUpdateTrends,
  setAuthToken,
  clearAuthToken,
  isAuthenticated,
} = apiService;

// Export api object for backward compatibility
export const api = {
  getTrends,
  getTrendById,
  getTrendAnalytics,
  compareTrends,
  getViralPredictions,
  getMomentumAnalysis,
  getForecastData,
  getBusinessInsights,
  getAudienceAnalysis,
  getAlerts,
  createAlert,
  updateAlert,
  deleteAlert,
  getAlertHistory,
  testAlert,
  getReports,
  generateReport,
  downloadReport,
  scheduleReport,
  getReportTemplates,
  getDashboards,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  getDashboardData,
  getApiUsage,
  getUsageAnalytics,
  getRateLimits,
  connectRealtime,
  getRealtimeData,
  subscribeToAlerts,
  getCustomDataSources,
  createCustomDataSource,
  testCustomDataSource,
  getCustomModels,
  createCustomModel,
  trainCustomModel,
  getModelPerformance,
  getCurrentUser,
  updateUserProfile,
  getUserPreferences,
  updateUserPreferences,
  getUserCompany,
  createUserCompany,
  updateUserCompany,
  deleteUserCompany,
  changePassword,
  enableTwoFactor,
  verifyTwoFactor,
  disableTwoFactor,
  getUserSessions,
  revokeUserSession,
  revokeAllUserSessions,
  exportUserData,
  deleteUserAccount,
  getWorkspaceSettings,
  updateWorkspaceSettings,
  getWhiteLabelConfig,
  updateWhiteLabelConfig,
  getIntegrations,
  createIntegration,
  testIntegration,
  getPlatformAnalytics,
  getCategoryAnalytics,
  getCompetitiveAnalysis,
  getMarketInsights,
  exportData,
  getDataQualityMetrics,
  getDataFreshness,
  getSystemHealth,
  getPipelineStatus,
  getPerformanceMetrics,
  getNotificationSettings,
  updateNotificationSettings,
  createNotificationSettings,
  sendTestNotification,
  searchTrends,
  getTrendSuggestions,
  getRelatedTrends,
  batchCreateAlerts,
  batchUpdateTrends,
  setAuthToken,
  clearAuthToken,
  isAuthenticated,
  getAuditLogs: apiService.getAuditLogs.bind(apiService),
  getUserActivitySummary: apiService.getUserActivitySummary.bind(apiService),
  getSecuritySummary: apiService.getSecuritySummary.bind(apiService),
  exportAuditLogs: apiService.exportAuditLogs.bind(apiService),
}; 